import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { ArrowLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import { useMediaQuery } from '@/hooks/common/useMediaQuery'

function Breadcrumb({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />
}

function BreadcrumbList({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={twMerge(
        'text-primary-700 dark:text-primary-300 flex items-center gap-1.5 text-sm sm:gap-2',
        'min-w-0 overflow-hidden whitespace-nowrap',
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbListItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={twMerge('inline-flex items-center gap-1.5 min-w-0', className)}
      {...props}
    />
  )
}
function BreadcrumbLink({
  asChild,
  className,
  href,
  ...props
}: React.ComponentProps<typeof Link> & {
  asChild?: boolean
}) {
  if (asChild) {
    return (
      <Slot
        data-slot="breadcrumb-link"
        className={twMerge(
          'hover:text-primary-900 dark:hover:text-primary-100 transition-colors duration-100',
          'block max-w-full',
          className,
        )}
        {...props}
      />
    )
  }

  return (
    <Link
      href={href}
      data-slot="breadcrumb-link"
      className={twMerge(
        'hover:text-primary-900 dark:hover:text-primary-100 transition-colors duration-100 cursor-pointer',
        'block max-w-full',
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbPage({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={twMerge(
        'text-primary-900 dark:text-primary-100 font-normal',
        'block max-w-full',
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={twMerge('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? (
        <ChevronRight
          size={14}
          className="text-primary-500 dark:text-primary-400 stroke-3"
        />
      )}
    </li>
  )
}

function BreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={twMerge('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4 text-primary-700 dark:text-primary-300" />
      <span className="sr-only">More</span>
    </span>
  )
}

export interface BreadcrumbItem {
  children: React.ReactNode
  href?: string | undefined
}

interface FullBreadcrumbListProps {
  items: BreadcrumbItem[]
  hrefPrefix?: string
}

/**
 * The standard Sweetspot breadcrumb trail, matching the chat panel header:
 * a compact root (usually the home icon), the parent level as the primary
 * `<- Parent` back control, a chevron, and the current location as a
 * highlighted non-interactive chip. Deeper ancestors render as plain links
 * between the root and the back control.
 */
function FullBreadcrumbList({
  items,
  hrefPrefix = '',
}: FullBreadcrumbListProps) {
  const isTablet = useMediaQuery('(max-width: 1024px)')
  const isMobile = useMediaQuery('(max-width: 768px)')

  const resolveHref = (href: string) =>
    `${hrefPrefix}${href}`.replace('//', '/')

  return (
    <BreadcrumbList className="gap-1 sm:gap-1">
      {items.map((item, index) => {
        const isLast = index === items.length - 1
        const isRoot = index === 0 && !isLast
        // The immediate parent is the primary control (the click people
        // reach for), so it carries the back arrow; it survives the tablet
        // collapse that drops deeper ancestors.
        const isParent = index === items.length - 2 && index > 0
        const isInbetween = index > 0 && index < items.length - 1 && !isParent
        // Mobile keeps the back control: the history-back icon buttons this
        // trail replaced were the only sub-768px way back to the parent list.
        if (isMobile && !isLast && !isParent) {
          return null
        }
        if (isTablet && isInbetween) {
          return null
        }
        // The chevron after the root drops only when the back-arrow parent is
        // the next visible crumb: always on 3-level trails, and on tablet for
        // deeper trails too (the inbetween ancestors are hidden there). Deep
        // desktop trails keep it — the crumb after the root is a plain link.
        const parentFollowsRoot =
          items.length === 3 || (isTablet && items.length > 3)
        const showSeparator = !isLast && !(isRoot && parentFollowsRoot)
        return (
          <React.Fragment key={index}>
            <BreadcrumbListItem
              className={isLast ? 'min-w-0 flex-1' : 'shrink-0 min-w-0'}
            >
              {isLast ? (
                <span
                  aria-current="page"
                  className="inline-flex min-w-0 max-w-full items-center gap-1.5 truncate rounded-md bg-primary-100/70 px-1.5 py-0.5 text-sm text-primary-950 shadow-sm shadow-black/[0.03] dark:bg-primary-800/70 dark:text-primary-50 dark:shadow-black/20"
                >
                  <span className="truncate">{item.children}</span>
                </span>
              ) : isRoot && item.href ? (
                <Link
                  href={resolveHref(item.href)}
                  className="inline-flex h-[26px] min-w-[26px] flex-shrink-0 items-center justify-center rounded-md px-1 text-primary-500 transition-colors hover:bg-primary-100 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-800 dark:hover:text-primary-100"
                >
                  {item.children}
                </Link>
              ) : isParent && item.href ? (
                <Link
                  href={resolveHref(item.href)}
                  aria-label={
                    typeof item.children === 'string'
                      ? `Back to ${item.children}`
                      : 'Back'
                  }
                  className="inline-flex min-w-0 max-w-[180px] shrink items-center gap-1.5 rounded-md px-1.5 py-0.5 text-sm text-primary-600 transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.2,0,0,1)] hover:bg-primary-100 hover:text-primary-900 active:scale-[0.96] dark:text-primary-300 dark:hover:bg-primary-800 dark:hover:text-primary-100"
                >
                  <ArrowLeft size={15} className="flex-shrink-0" />
                  <span className="truncate">{item.children}</span>
                </Link>
              ) : item.href ? (
                <BreadcrumbLink
                  href={resolveHref(item.href)}
                  className="truncate"
                >
                  {item.children}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage className="truncate text-primary-700 dark:text-primary-300">
                  {item.children}
                </BreadcrumbPage>
              )}
            </BreadcrumbListItem>
            {showSeparator && <BreadcrumbSeparator />}
          </React.Fragment>
        )
      })}
    </BreadcrumbList>
  )
}

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbListItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
  FullBreadcrumbList,
}
